// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/LoginTeaser.tsx
// 🔧 PURPOSE: "Con l'accesso · Tutto il soggiorno nel telefono" della vista
//             ospite: sei tessere col lucchetto (Codice porta, WiFi, Chat
//             24h, Extra in casa, Pulizie, Il tuo soggiorno) e ACCEDI.
//             Ogni tessera apre il login e, SOLO se l'accesso riesce, porta
//             l'ospite dove voleva andare (openLogin(returnTo) di
//             HomeSessionHost; piano §4):
//               codice porta → /my-stay?open=door, WiFi → ?open=wifi,
//               chat → ?open=chat, extra → /extras, pulizie →
//               /extras/extra_cleaning, soggiorno → /my-stay.
//             Le pagine fanno poi i loro controlli: il codice e il WiFi
//             restano legati al check-in e alle date del soggiorno.
// ============================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Briefcase, KeyRound, Lock, MessageCircle, ShoppingBag, Sparkles, Wifi, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useHomeSession } from './HomeSessionHost';
import { CardShell, Eyebrow, FOCUS_RING, INK_TEXT, InkButton, Section } from './ui';

type TeaserKey = 'door' | 'wifi' | 'chat' | 'extras' | 'cleaning' | 'stay';

/** Le tessere e dove portano dopo l'accesso. */
export const TEASER_ITEMS: { key: TeaserKey; icon: LucideIcon; returnTo: string }[] = [
  { key: 'door', icon: KeyRound, returnTo: '/my-stay?open=door' },
  { key: 'wifi', icon: Wifi, returnTo: '/my-stay?open=wifi' },
  { key: 'chat', icon: MessageCircle, returnTo: '/my-stay?open=chat' },
  { key: 'extras', icon: ShoppingBag, returnTo: '/extras' },
  { key: 'cleaning', icon: Sparkles, returnTo: '/extras/extra_cleaning' },
  { key: 'stay', icon: Briefcase, returnTo: '/my-stay' },
];

const TILE =
  'flex min-h-[44px] w-full items-center gap-2 rounded-xl border border-nva-hairline bg-nva-bone px-2.5 py-2 text-left transition-colors ' +
  'hover:bg-nva-cream/70 dark:border-stone-700 dark:bg-stone-800 dark:hover:bg-stone-700/70 motion-safe:active:scale-[0.98]';

const LoginTeaser: React.FC<{ className?: string }> = ({ className }) => {
  const { t } = useTranslation();
  const { openLogin } = useHomeSession();
  const titleId = 'home-guest-teaser-title';

  return (
    <Section labelledBy={titleId} className={className}>
      <CardShell className="flex flex-col gap-3 p-3.5">
        <div className="flex flex-col gap-[3px]">
          <Eyebrow>{t('home.guest.teaser.eyebrow')}</Eyebrow>
          <h2 id={titleId} className={cn('m-0 font-display text-[20px] font-bold leading-tight', INK_TEXT)}>
            {t('home.guest.teaser.title')}
          </h2>
        </div>
        <ul className="m-0 grid list-none grid-cols-2 gap-2 p-0">
          {TEASER_ITEMS.map(({ key, icon: Icon, returnTo }) => {
            const name = t(`home.guest.teaser.items.${key}`);
            return (
              <li key={key} className="min-w-0">
                <button
                  type="button"
                  onClick={() => openLogin(returnTo)}
                  aria-label={t('home.guest.teaser.itemAria', { item: name })}
                  className={cn(TILE, FOCUS_RING)}
                >
                  <Icon
                    className="h-4 w-4 flex-shrink-0 text-nva-gold-text dark:text-nva-gold-dark"
                    strokeWidth={2}
                    aria-hidden="true"
                  />
                  <span className={cn('min-w-0 flex-grow break-words text-[13.5px] font-bold leading-tight', INK_TEXT)}>
                    {name}
                  </span>
                  <Lock className="h-[13px] w-[13px] flex-shrink-0 text-nva-muted dark:text-stone-400" strokeWidth={2.2} aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
        <InkButton full onClick={() => openLogin()}>
          {t('home.guest.teaser.cta')}
        </InkButton>
      </CardShell>
    </Section>
  );
};

export default LoginTeaser;
